import { Grid, Typography } from "@mui/material";
import { useTransactions } from "api/transactions";
import { OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { SINGLE_TRANSACTION_RELATED_TRANSACTIONS } from "state/tables/types";
import { ListingActions } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { capitalizeFirstLetter } from "utilities/strings";
import { useStyles } from "./ProductsSection.styles";

interface Props {
  type: "sell" | "return";
  documentNumber: string;
}

export const RelatedTransactions = ({ type, documentNumber }: Props) => {
  const { listing, updateListing } = useListing(
    SINGLE_TRANSACTION_RELATED_TRANSACTIONS,
  );
  const { useTransactionsList } = useTransactions();
  const { message } = useMessage();
  const classes = useStyles();
  const navigate = useNavigate();

  const { data } = useTransactionsList({
    filters: [
      {
        query:
          type === "sell"
            ? `header:linkedDocumentNumber`
            : `header:documentNumber`,
        condition: "",
        value: documentNumber,
      },
    ],
    page: listing.page,
    rows: listing.rowsPerPage,
  });

  const headItems = createHeadItems([
    {
      headerText: message({ id: "documentNumber" }),
      property: "header:documentNumber",
      isSortable: false,
    },
    {
      headerText: message({ id: "documentType" }),
      property: "header:documentType",
      isSortable: false,
    },
    {
      headerText: message({ id: "documentDate" }),
      property: "header:purchasedAt",
      isSortable: false,
    },
    {
      headerText: message({ id: "fullName" }),
      property: "customerData:name",
      isSortable: false,
    },
    {
      headerText: message({ id: "email" }),
      property: "customerData:email",
      hasMinWidthThreshold: true,
      isSortable: false,
    },
  ]);

  const createItems = useMemo((): Row[] => {
    return data!?.items.map((item) => ({
      cells: [
        {
          content: item.header?.documentNumber,
          property: "header:documentNumber",
        },
        {
          content:
            item.header?.documentType === "sell"
              ? message({ id: "purchase" })
              : capitalizeFirstLetter(item.header?.documentType as string),
          property: "header:documentType",
        },
        {
          content: OLDate(item.header?.purchasedAt),
          property: "header:purchasedAt",
        },
        { content: item.customerData?.name, property: "customerData:name" },
        { content: item.customerData?.email, property: "customerData:email" },
      ],
      id: item.transactionId,
      metadata: { customerId: item.customerId },
    }));
  }, [data, message]);

  const items = data?.items ? createItems : [];

  return (
    <Grid item xs={12}>
      <Typography className={classes.sectionTitle} marginBottom={6}>
        {message(
          { id: "transactionsRelated" },
          {
            type: message({
              id: type === "sell" ? "purchase" : "return",
            })?.toLowerCase(),
          },
        )}
      </Typography>
      <OLTable
        disableColumnManager
        tableName={SINGLE_TRANSACTION_RELATED_TRANSACTIONS}
        data-test-id="transactions-related"
        sort={{
          order: "asc",
          property: "",
          rowsPerPage: 10,
          page: 1,
        }}
        columns={data && headItems}
        rows={data && items}
        onSort={() => null}
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => navigate(links.transaction(id as string)),
              label: message({ id: "seeDetails" }),
            },
          ],
        }}
        pagination={{
          count: calculateCount(data?.total.filtered, data?.total.all),
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
          onChangePage: (page) => {
            updateListing({ type: ListingActions.CHANGE_PAGE, payload: page });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
              payload: rows,
            });
          },
        }}
      />
    </Grid>
  );
};
